import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { CompanyType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { debounce, snakeCase, uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AppliedFiltersChips, {
  AppliedFiltersChipsCollectedData,
} from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { AccountSelectedFilter } from '~constants/enums';
import {
  OrderDispatchQuery,
  useCompanyTypesQuery,
  useDepartmentsQuery,
} from '~pages/Dispatch/components/filters/orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';
import { useStores } from '~store/RootStore';
import isEnumValue from '~utils/isEnumValue';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { useAccountLabelsQuery } from './accountFiltersQueries';
import {
  accountFilterKeyBySelectedFilter,
  Filters,
  useAccountFilters,
} from './useAccountFilters';

const noneOptionFilters = [AccountSelectedFilter.DEPARTMENTS];

const AccountFilters = observer(() => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<AccountSelectedFilter>();
  const { userStore, companyAssetsStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    appliedFilterChipsCollectedData,
    updateAvailableOptions,
    updateAppliedFilterChipsCollectedData,
  } = useAccountFilters();

  const [searchQuery, setSearchQuery] = useState<{ input: string; debounced: string }>({
    input: '',
    debounced: '',
  });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const departmentsQuery = useDepartmentsQuery(companyId);
  const companyTypesQuery = useCompanyTypesQuery();
  const labelsQuery = useAccountLabelsQuery(companyId);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const queries: Record<AccountSelectedFilter, OrderDispatchQuery> = {
      [AccountSelectedFilter.DEPARTMENTS]: departmentsQuery,
      [AccountSelectedFilter.COMPANY_TYPES]: companyTypesQuery,
      [AccountSelectedFilter.LABELS]: labelsQuery,
    };

    return queries[selectedFilter];
  }, [selectedFilter, departmentsQuery, companyTypesQuery, labelsQuery]);

  const searchValueChangeCallback = useMemo(() => {
    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, []);

  const filterChipsData = useMemo(() => {
    const appliedFilters: Record<string, string[]> = {};

    if (companyAssetsStore.accountFilters.departments) {
      appliedFilters.departments = companyAssetsStore.accountFilters.departments;
    }

    if (companyAssetsStore.accountFilters.connected_company?.company_types) {
      appliedFilters['connected_company.company_types'] =
        companyAssetsStore.accountFilters.connected_company.company_types;
    }

    if (companyAssetsStore.accountFilters.labels) {
      appliedFilters.labels = companyAssetsStore.accountFilters.labels;
    }

    const collectedData = Object.entries(appliedFilterChipsCollectedData).reduce(
      (obj, [key, collectedValues]) => {
        const filter = accountFilterKeyBySelectedFilter[key as AccountSelectedFilter];

        obj[filter] = collectedValues;

        return obj;
      },
      {} as AppliedFiltersChipsCollectedData,
    );

    return { appliedFilters, collectedData };
  }, [
    JSON.stringify(companyAssetsStore.accountFilters),
    appliedFilterChipsCollectedData,
  ]);

  const hasFiltersApplied = useMemo(() => {
    const { appliedFilters } = filterChipsData;

    return Object.values(appliedFilters).filter((value) => Boolean(value)).length > 0;
  }, [JSON.stringify(filterChipsData.appliedFilters)]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = accountFilterKeyBySelectedFilter[selectedFilter];
    if (key === 'connected_company.company_types') {
      return companyAssetsStore.accountFilters.connected_company?.company_types ?? [];
    }

    const options = companyAssetsStore.accountFilters[key as keyof Filters];
    return (options as string[]) ?? [];
  }, [companyAssetsStore.accountFilters, selectedFilter]);

  const handleSelectedOptionsChange = useCallback(
    (selectedOptions: string[]) => {
      if (selectedFilter) {
        const key = accountFilterKeyBySelectedFilter[selectedFilter];
        const options = selectedOptions.length > 0 ? selectedOptions : undefined;

        if (key === 'connected_company.company_types') {
          const companyTypes = options?.filter((option) =>
            isEnumValue(CompanyType, option),
          );

          companyAssetsStore.setAccountFilters(
            { connected_company: { company_types: companyTypes } },
            true,
          );
        } else {
          companyAssetsStore.setAccountFilters({ [key]: options }, true);
        }
      }
    },
    [selectedFilter],
  );

  const fetchMoreCallback = useMemo(() => {
    return activeQuery?.fetchMore;
  }, [activeQuery?.fetchMore]);

  const handleRemoveAllFilters = useCallback((filters: string | string[]) => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];
    const filtersObject = Object.fromEntries(
      filtersArray.map((filter) => {
        if (filter === 'connected_company.company_types') {
          return ['connected_company', { company_types: undefined }];
        }

        return [filter, undefined];
      }),
    );

    companyAssetsStore.setAccountFilters(filtersObject, true);
  }, []);

  const updateAvailableOptionsAndFilterChips = useCallback(
    (key: AccountSelectedFilter, data: FilterMenuOption[]) => {
      updateAvailableOptions((state) => {
        return { ...state, [key]: uniqBy([...state[key], ...data], (i) => i.value) };
      });

      updateAppliedFilterChipsCollectedData(key, data);
    },
    [],
  );

  const getNoneOptionLabel = (selectedFilter: AccountSelectedFilter) => {
    if (noneOptionFilters.includes(selectedFilter)) {
      return `${t('dispatch.dispatch_v2.filters.no_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`;
    }
  };

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    if (selectedFilter && activeQuery?.data) {
      updateAvailableOptionsAndFilterChips(selectedFilter, activeQuery?.data);
    }
  }, [updateAvailableOptionsAndFilterChips, selectedFilter, activeQuery?.data]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      departmentsQuery.clear();
      companyTypesQuery.clear();
      labelsQuery.clear();
    }
  }, [
    selectedFilter,
    departmentsQuery.clear,
    companyTypesQuery.clear,
    labelsQuery.clear,
  ]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={{
              [`&.${buttonClasses.root}`]: {
                backgroundColor: theme.semantic.surfaceRaised,
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                ...(hasFiltersApplied
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            }}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            searchValue={searchQuery.input}
            onSearchValueChange={searchValueChangeCallback}
            loadingReason={activeQuery?.loadingReason}
            selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`}
            options={activeQuery?.data ?? []}
            selectedOptions={selectedOptions}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            onFetchMore={fetchMoreCallback}
            selectNoneOptionLabel={getNoneOptionLabel(selectedFilter)}
          />
        )}
        {!selectedFilter &&
          Object.values(AccountSelectedFilter).map((filter) => (
            <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                  {t(`dispatch.dispatch_v2.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {(() => {
                    const key = accountFilterKeyBySelectedFilter[filter];
                    if (key === 'connected_company.company_types') {
                      return (
                        (
                          companyAssetsStore.accountFilters.connected_company
                            ?.company_types ?? []
                        ).length > 0
                      );
                    }
                    return (
                      (
                        (companyAssetsStore.accountFilters[
                          key as keyof Filters
                        ] as string[]) ?? []
                      ).length > 0
                    );
                  })() && (
                    <Check
                      sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>
      <AppliedFiltersChips
        {...filterChipsData}
        onRemoveFilter={handleRemoveAllFilters}
        onRemoveAllFilters={handleRemoveAllFilters}
      />
    </Box>
  );
});

export default AccountFilters;
